/**
 * The change model: what moves between devices, and what a device is allowed to
 * do with it.
 *
 * WHY THIS IS DOMAIN AND NOT A TRANSPORT TYPE
 * -------------------------------------------
 * Because these are ACCOUNTING facts wearing a sync shape. That a stock movement
 * is append-only, that an issued invoice is not overwritable, and that a
 * deletion travels as a tombstone are rules about the business, not about
 * Dropbox. Putting them here means a provider cannot be written that quietly
 * violates them — the engine asks this module what is safe and applies the
 * answer, whatever the transport happens to be.
 *
 * The alternative — letting each adapter decide — produced 1.6.x, where the same
 * rule was implemented slightly differently in each screen.
 */

/** A provider identifier. A union, so an unimplemented provider is a type error. */
export type ProviderId =
  | 'supabase'
  | 'dnr-cloud'
  | 'google-drive'
  | 'onedrive'
  | 'dropbox'
  | 's3'
  | 'webdav'
  | 'memory'
  | 'fake';

export const PROVIDER_IDS: readonly ProviderId[] = [
  'supabase',
  'dnr-cloud',
  'google-drive',
  'onedrive',
  'dropbox',
  's3',
  'webdav',
  'memory',
  'fake',
];

/** Which table a change refers to. Explicit, never a raw string from the wire. */
export type SyncEntityType =
  | 'party'
  | 'product'
  | 'category'
  | 'taxRate'
  | 'stockLocation'
  | 'document'
  | 'documentLine'
  | 'documentLink'
  | 'payment'
  | 'paymentAllocation'
  | 'expense'
  | 'stockTransaction'
  | 'business';

/**
 * The business tables a change may refer to.
 *
 * An allow-list, because a sync payload arrives from outside and must never be
 * able to name a table. Without it, a malformed or hostile change could ask the
 * engine to write `settings` or `auditLogs` — the two tables that are not
 * business data and whose corruption is unrecoverable.
 */
export const SYNCABLE_ENTITIES: readonly SyncEntityType[] = [
  'party',
  'product',
  'category',
  'taxRate',
  'stockLocation',
  'document',
  'documentLine',
  // Links are syncable and MANDATORY, not optional. A device that received a
  // Delivery Note and its invoice but not the DELIVERED_AGAINST link between
  // them would let the same goods be deducted twice: once by the note and again
  // by the invoice, because nothing says the invoice already had them.
  'documentLink',
  'payment',
  'paymentAllocation',
  'expense',
  'stockTransaction',
  'business',
];

/**
 * What the change does.
 *
 * `DELETE` is a TOMBSTONE, never a physical delete. A device that never receives
 * the deletion will re-upload the row on its next push and resurrect it, which is
 * how a deleted customer comes back from the dead a week later.
 */
export type SyncOperation = 'UPSERT' | 'DELETE';

export interface SyncChange {
  /**
   * Stable across every retry. Generated ONCE, when the local write happened.
   *
   * This is the whole idempotency mechanism. A push that succeeds remotely and
   * fails locally is retried, and the retry carries the same id, so a correct
   * provider recognises it and does nothing. A duplicated invoice or payment is a
   * real accounting event, not a sync artefact.
   */
  readonly changeId: string;
  readonly businessId: string;
  readonly entityType: SyncEntityType;
  readonly entityId: string;
  readonly operation: SyncOperation;
  /**
   * Per-entity version, monotonic per `(businessId, entityType, entityId)`.
   *
   * Lets a device answer "am I behind?" without a round trip, and lets the engine
   * tell a genuine conflict from a duplicate.
   */
  readonly version: number;
  readonly deviceId: string;
  /** ISO timestamp of the LOCAL write, not of the push. */
  readonly changedAt: string;
  /**
   * The row, for UPSERT. Absent for DELETE.
   *
   * Opaque here: the engine hands it to Dexie and this module does not interpret
   * it. A change that does not need reading to be moved should not have to be
   * understood to be moved.
   */
  readonly payload?: Readonly<Record<string, unknown>>;
  /** Set on a DELETE, so a receiving device knows this is a tombstone. */
  readonly deletedAt?: string;
}

/** An opaque position in the remote change log. Compared, never parsed. */
export interface SyncCursor {
  readonly token: string;
}

/**
 * What a provider can actually do. The engine READS this and adapts.
 *
 * The distinction that matters most is `transactions`: a managed cloud can apply
 * fifty changes atomically and Google Drive cannot do anything atomic. An engine
 * that assumed atomicity would tell a Drive user their data was safely written
 * when it was half written.
 */
export interface SyncCapabilities {
  /** Can the provider return only what changed since a cursor? */
  readonly incrementalSync: boolean;
  /** Does the provider push changes without being polled? */
  readonly realtime: boolean;
  /** Can the provider filter server-side rather than returning everything? */
  readonly deltaQuery: boolean;
  /** Can it write several rows as one indivisible unit? */
  readonly transactions: boolean;
  /** Is a single write all-or-nothing? */
  readonly atomicWrite: boolean;
  /** Does it keep versions itself, or must we? */
  readonly serverVersioning: boolean;
  /** Is it a file store rather than a row store? */
  readonly fileStorage: boolean;
  /** Can it delete? Object-locked buckets and write-once media cannot. */
  readonly delete: boolean;
  /** Can it hold a full snapshot? */
  readonly snapshots: boolean;
  /** Largest change batch it accepts. The engine never exceeds it. */
  readonly maxBatchSize: number;
}

/** The capability set of a plain managed row store — the optimistic case. */
export const MANAGED_ROW_STORE: SyncCapabilities = {
  incrementalSync: true,
  realtime: true,
  deltaQuery: true,
  transactions: true,
  atomicWrite: true,
  serverVersioning: true,
  fileStorage: false,
  delete: true,
  snapshots: true,
  maxBatchSize: 500,
};

/**
 * The capability set of a file folder — Google Drive, OneDrive, Dropbox, S3, WebDAV.
 *
 * Note what is FALSE: no transactions, no atomic multi-row write, no delta query,
 * no server-side versioning. An engine that ignores this and posts a 200-row
 * batch into a shared folder is relying on a provider to serialise writes it has
 * no facility to serialise.
 */
export const FILE_STORAGE: SyncCapabilities = {
  incrementalSync: true,
  realtime: false,
  deltaQuery: false,
  transactions: false,
  atomicWrite: false,
  serverVersioning: false,
  fileStorage: true,
  delete: true,
  snapshots: true,
  // Small, because each change is a separate object and providers rate-limit.
  maxBatchSize: 25,
};

/** Why a change was refused. Every code is one the engine can act on. */
export type ChangeRejection =
  | 'UNKNOWN_ENTITY'
  | 'MISSING_BUSINESS'
  | 'BUSINESS_MISMATCH'
  | 'MISSING_ID'
  | 'BAD_VERSION'
  | 'DELETE_WITHOUT_TIMESTAMP'
  | 'INVALID_DELETE_TIMESTAMP'
  | 'UPSERT_WITHOUT_PAYLOAD';

export type ChangeValidation =
  | { readonly ok: true; readonly change: SyncChange }
  | { readonly ok: false; readonly reason: ChangeRejection; readonly detail: string };

/**
 * Validate an incoming change BEFORE it is applied locally.
 *
 * A change is untrusted input: it arrived from another machine, possibly through
 * a provider that mangles data, possibly from a version of the app with different
 * assumptions. Applying it unchecked is how a cross-business row, a negative
 * version, or a write to a non-syncable table gets in.
 *
 * Note what this does NOT do: it does not decide whether the change is
 * *acceptable* to the business. That is the conflict model's job. This only
 * answers "is this shaped like a change at all".
 */
export function validateChange(input: unknown, expectedBusinessId: string): ChangeValidation {
  if (typeof input !== 'object' || input === null) {
    return { ok: false, reason: 'MISSING_BUSINESS', detail: 'not an object' };
  }
  const c = input as Partial<SyncChange>;

  if (!c.changeId || typeof c.changeId !== 'string') {
    return { ok: false, reason: 'MISSING_ID', detail: 'changeId is required' };
  }
  if (!c.entityId || typeof c.entityId !== 'string') {
    return { ok: false, reason: 'MISSING_ID', detail: 'entityId is required' };
  }
  if (!c.entityType || !SYNCABLE_ENTITIES.includes(c.entityType)) {
    return { ok: false, reason: 'UNKNOWN_ENTITY', detail: String(c.entityType) };
  }

  // The isolation gate, and the most important check in this file: a change for
  // another business is REFUSED, never "applied to whichever business happens to
  // be open". A compromised or buggy provider must not be able to write
  // Business A's rows into Business B by naming the wrong id.
  if (c.businessId !== expectedBusinessId) {
    return {
      ok: false,
      reason: 'BUSINESS_MISMATCH',
      detail: `change for ${String(c.businessId)}, expected ${expectedBusinessId}`,
    };
  }

  if (!Number.isInteger(c.version) || (c.version as number) < 1) {
    return { ok: false, reason: 'BAD_VERSION', detail: String(c.version) };
  }
  if (c.operation !== 'UPSERT' && c.operation !== 'DELETE') {
    return { ok: false, reason: 'BAD_VERSION', detail: `unknown operation ${String(c.operation)}` };
  }
  if (c.operation === 'DELETE' && !c.deletedAt) {
    // A tombstone with no timestamp cannot be aged out during compaction, and an
    // un-ageable tombstone means the row can never be safely removed.
    return { ok: false, reason: 'DELETE_WITHOUT_TIMESTAMP', detail: 'deletedAt is required' };
  }
  if (
    c.operation === 'DELETE' &&
    (typeof c.deletedAt !== 'string' || Number.isNaN(Date.parse(c.deletedAt)))
  ) {
    return {
      ok: false,
      reason: 'INVALID_DELETE_TIMESTAMP',
      detail: 'deletedAt must be a valid timestamp',
    };
  }
  if (c.operation === 'UPSERT' && (!c.payload || typeof c.payload !== 'object')) {
    return { ok: false, reason: 'UPSERT_WITHOUT_PAYLOAD', detail: 'payload is required' };
  }

  return { ok: true, change: input as SyncChange };
}

/** Compare two cursors. The token is opaque, so this is the ONLY way to order them. */
export function cursorIsAfter(a: SyncCursor | null, b: SyncCursor | null): boolean {
  if (!a || !b) return false;
  return a.token > b.token;
}
